import {
  Box,
  Button,
  Modal,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
  DialogContent,
  TextField,
  FormControl,
  Select,
  OutlinedInput,
  MenuItem
} from "@mui/material"
import {
  TerminalRounded,
  CloseRounded,
  AddCircleOutlineRounded,
  ReplayRounded,
  EditRounded,
  ContentCopyRounded
} from '@mui/icons-material'
import {
  useEffect,
  useState,
  useReducer,
  ReactNode
} from "react"
import { deviceImage } from "./device"
import { Embosser } from "../providers/embossers"
import useWidth from "../hooks/window"
import { useTheme } from "../providers/theme"
import { useAuth } from "../providers/auth"
import { makeStyles } from "@mui/styles"
import {
  styles,
  stylesDark,
  Styles
} from "../providers/theme"
import makeToast from "./toast"
import {socket} from "../index"
import {
  PropertyArea,
  MultiplePropertiesArea
} from "./propertyArea"
import { LoadingButton } from "@mui/lab"

const useStylesLight = makeStyles<any, Styles>({...styles})
const useStylesDark = makeStyles<any, Styles>({...stylesDark})

interface DialogState {
  title: string;
  description: string;
  property: string;
  inputType: string;
  inputLabel: string;
  itemValue: any;
  itemsList: string[];
  request: any;
  requestData: boolean;
  requestDataKey: string;
  additionalSocketSignal: string;
  additionalSocketSignalData: any;
  dialogActionButtonLabel: string;
  dialogActionButtonLoading: boolean;
  dialogActionButtonOnClick: () => void;
}

interface EmbosserCardState {
  modalOpen: boolean;
  dialogOpen: boolean;
}

export default function EmbosserCard({ embosser, displayMode }: { embosser: Embosser, displayMode: string }) {
  const {currentRole, currentTimeZoneOffset} = useAuth()
  const localStartDateTime = new Date(new Date(`${embosser.connected.date} ${embosser.connected.hour}`).getTime() - currentTimeZoneOffset)
  const [localCurrentDateTime, setLocalCurrentDateTime] = useState<Date>(new Date())
  const [elapsedTime, setElapsedTime] = useState<string>('')
  const [reconnectRequested, setReconnectRequested] = useState<boolean>(false)
  const [timeRemaining, setTimeRemaining] = useState<string>('')
  const [increaseTimeRemainingDelay, setIncreaseTimeRemainingDelay] = useState<boolean>(false)
  const width = useWidth()
  const { mode } = useTheme()
  let classesLight = useStylesLight(styles)
  let classesDark = useStylesDark(stylesDark)
  const classes = mode === 'light' ? classesLight : classesDark
  const webInterfaceVersions = ["v230111", "v231017", "v240402", "v240625", "v241029"]
  const [dialogInput, setDialogInput] = useState<ReactNode | null>(<></>)

  const [embosserState, dispatchEmbosserState] = useReducer((currentState: EmbosserCardState, newState: Partial<EmbosserCardState>) => ({
    ...currentState,
    ...newState
  }), {
    // Modal
    modalOpen: false,
    // Dialog
    dialogOpen: false
  })
  const [dialogState, dispatchDialogState] = useReducer((currentState: DialogState, newState: Partial<DialogState>) => ({
    ...currentState,
    ...newState
  }), {
    title: '',
    description: '',
    property: '',
    inputType: '',
    inputLabel: '',
    itemValue: '',
    itemsList: [],
    request: {},
    requestData: false,
    requestDataKey: '',
    additionalSocketSignal: '',
    additionalSocketSignalData: {},
    dialogActionButtonLabel: '',
    dialogActionButtonLoading: false,
    dialogActionButtonOnClick: () => {}
  })

  useEffect(() => {
    const inputType = dialogState.inputType.toLowerCase()
    if (inputType === 'textfield') {
      setDialogInput(
        <div
          style={{
            width: "100%",
            marginTop: "1em",
            marginBottom: "1em",
            display: "flex",
            flexDirection: "column"
          }}
        >
          <TextField
            sx={{ marginTop: "1em" }}
            label={dialogState.inputLabel}
            value={dialogState.itemValue}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const newItemValue = event.target.value
              let newRequest = dialogState.request
              let newAdditionalSocketSignalData = dialogState.additionalSocketSignalData
              if (dialogState.requestData) {
                newRequest = {
                  "request": dialogState.request.request,
                  "data": {
                    [`${dialogState.requestDataKey}`]: newItemValue
                  }
                }
              }
              if (dialogState.additionalSocketSignal !== "") {
                newAdditionalSocketSignalData = {
                  ...dialogState.additionalSocketSignalData,
                  [`${dialogState.requestDataKey}`]: newItemValue
                }
              }
              dispatchDialogState({
                itemValue: newItemValue,
                request: newRequest,
                additionalSocketSignalData: newAdditionalSocketSignalData
              })
            }}
          />
        </div>
      )
    } else if (inputType.includes('select')) {
      setDialogInput(
        <FormControl sx={{ width: "100%", mt: 3, mb: 3 }}>
          <Select
            value={dialogState.itemValue}
            onChange={(event) => {
              const { target: { value } } = event
              let newRequest = dialogState.request
              let newAdditionalSocketSignalData = dialogState.additionalSocketSignalData
              if (dialogState.requestData) {
                newRequest = {
                  "request": dialogState.request.request,
                  "data": {
                    [`${dialogState.requestDataKey}`]: value
                  }
                }
              }
              if (dialogState.additionalSocketSignal !== "") {
                newAdditionalSocketSignalData = {
                  ...dialogState.additionalSocketSignalData,
                  [`${dialogState.requestDataKey}`]: value
                }
              }
              dispatchDialogState({
                itemValue: value,
                request: newRequest,
                additionalSocketSignalData: newAdditionalSocketSignalData
              })
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: mode === 'light' ? "#fff" : "#151515",
                  borderRadius: '10px',
                  '&::-webkit-scrollbar': {
                    width: ".3em",
                  },
                  '&::-webkit-scrollbar-track': {
                    background: "transparent",
                    boxShadow: "inset 0 0 10px #1c1c1c",
                    borderRadius: "5px",
                    borderLeft: "1px solid transparent",
                    borderRight: "1px solid transparent",
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#444',
                    outline: '1px solid #444',
                    borderRadius: ".15em"
                  }
                }
              }
            }}
            input={<OutlinedInput />}
          >
          {
            dialogState.itemsList !== undefined ?
              dialogState.itemsList.map((item: string) => (
                <MenuItem
                  key={item}
                  value={item}
                  sx={{
                    "&.Mui-selected": {
                      backgroundColor: "rgba(133, 210, 1, 0.3)",
                    },
                    "&.Mui-selected:hover": {
                      backgroundColor: "rgba(133, 210, 1, 0.3)",
                    },
                    "&:hover": {
                      backgroundColor: `${mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"}`
                    }
                  }}
                >
                  {item}
                </MenuItem>
              ))
            : null
          }
          </Select>
        </FormControl>
      )
    } else if (inputType === 'none') {
      setDialogInput(null)
    }
    // eslint-disable-next-line
  }, [dialogState.itemValue, dialogState.inputType])

  const getLastUpdateVersion = (date: string) => {
    if (!date) {
      return "Invalid";
    }
    const dateObject = new Date(date)
    const day = dateObject.getDate() < 10 ? `0${dateObject.getDate()}` : `${dateObject.getDate()}`
    let month = dateObject.getMonth() + 1
    const monthString = month < 10 ? `0${month}` : `${month}`
    const year = dateObject.getFullYear() % 100
    const version = `v${year}${monthString}${day}`
    return version;
  }

  const getWebInterfaceVersion = (date: string) => {
    const version = getLastUpdateVersion(date)
    if (version === "Invalid") {
      return version
    }
    if (version < webInterfaceVersions.at(0)!) {
      return version;
    }
    let index = 1
    while (index < webInterfaceVersions.length) {
      const webInterfaceVersion = webInterfaceVersions.at(index)
      if (webInterfaceVersion! > version) {
        return webInterfaceVersions.at(index - 1)
      }
      index += 1
    }
    return webInterfaceVersions.at(webInterfaceVersions.length - 1)
  }

  const calculateElapsedTime = () => {
    let timeRemainingMiliseconds = parseInt(embosser.connected.timeRemaining) * (1000 * 60 * 60 * 24)
    let elapsedTimeMiliseconds = Math.floor(localCurrentDateTime.getTime() - localStartDateTime.getTime())
    timeRemainingMiliseconds = timeRemainingMiliseconds - elapsedTimeMiliseconds

    const days = Math.floor(elapsedTimeMiliseconds / (1000 * 60 * 60 * 24))
    elapsedTimeMiliseconds = elapsedTimeMiliseconds - (days * 1000 * 60 * 60 * 24)
    const hours = Math.floor(elapsedTimeMiliseconds / (1000 * 60 * 60))
    const minutes = Math.floor((elapsedTimeMiliseconds - (hours * 1000 * 60 * 60)) / (1000 * 60))
    const elapsedTime = `${days} ${days === 1 ? 'day' : 'days'}, ${hours} ${hours === 1 ? 'hour' : 'hours'}, ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
    setElapsedTime(elapsedTime)

    const daysRemaining = Math.floor(timeRemainingMiliseconds / (1000 * 60 * 60 * 24))
    timeRemainingMiliseconds = timeRemainingMiliseconds - (daysRemaining * 1000 * 60 * 60 * 24)
    const hoursRemaining = Math.floor(timeRemainingMiliseconds / (1000 * 60 * 60))
    const minutesRemaining = Math.floor((timeRemainingMiliseconds - (hoursRemaining * 1000 * 60 * 60)) / (1000 * 60))
    const timeRemaining = `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}, ${hoursRemaining} ${hoursRemaining === 1 ? 'hour' : 'hours'}, ${minutesRemaining} ${minutesRemaining === 1 ? 'minute' : 'minutes'}`
    setTimeRemaining(timeRemaining)
  }

  useEffect(() => {
    let timeout :  ReturnType<typeof setTimeout>
    if (increaseTimeRemainingDelay) {
      timeout = setTimeout(() => {
        setIncreaseTimeRemainingDelay(false)
      }, 2000)
    }
    return () => {
      if (increaseTimeRemainingDelay) {
        clearTimeout(timeout)
      }
    }
  }, [increaseTimeRemainingDelay])

  useEffect(() => {
    const interval = setInterval(() => {
      setLocalCurrentDateTime(new Date())
    }, 60000)
    return () => {
      clearInterval(interval)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    calculateElapsedTime()
    // eslint-disable-next-line
  }, [localCurrentDateTime, embosser])

  return (
    <>
      {
        displayMode === 'grid' ?
          <Card className={classes.embosserCard}>
            <CardMedia
              component="img"
              height="200"
              alt={embosser.model}
              src={deviceImage(embosser.model)}
              className={classes.embosserCardMedia}
            />
            <CardContent>
              <PropertyArea
                property={"Serial"}
                content={embosser.serial !== '' ? embosser.serial : "Unknown"}
                classes={{
                  container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']}`,
                  property: classes['propertyAreaProperty'],
                  content: classes['propertyAreaContent']
                }}
              />
              <PropertyArea
                property={"Model"}
                content={embosser.model !== '' ? embosser.model : "Unknown"}
                classes={{
                  container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']}`,
                  property: classes['propertyAreaProperty'],
                  content: classes['propertyAreaContent']
                }}
              />
              <PropertyArea
                property={"Type"}
                content={embosser.type !== '' ? embosser.type : "Unknown"}
                classes={{
                  container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']}`,
                  property: classes['propertyAreaProperty'],
                  content: classes['propertyAreaContent']
                }}
              />
              <PropertyArea
                property={"Hostname"}
                content={embosser.hostname !== '' ? embosser.hostname : "Unknown"}
                classes={{
                  container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']}`,
                  property: classes['propertyAreaProperty'],
                  content: classes['propertyAreaContent']
                }}
              />
              <PropertyArea
                property={"Connected On"}
                content={embosser.connected !== undefined ? `${localStartDateTime.toLocaleDateString((navigator as any).language || (navigator as any).userLanguage, { year: 'numeric', month: 'short', day: 'numeric' })}, ${localStartDateTime.toLocaleTimeString()}` : "Unknown"}
                classes={{
                  container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']}`,
                  property: classes['propertyAreaProperty'],
                  content: classes['propertyAreaContent']
                }}
              />
            </CardContent>
            <CardActions sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                className={classes.embosserCardButtonOpen}
                onClick={() => {
                  dispatchEmbosserState({modalOpen: true})
                }}
                size="small"
              >
                Open
              </Button>
              <Button
                variant="contained"
                color="error"
                className={classes.embosserCardButtonDisconnect}
                onClick={() => {
                  dispatchDialogState({
                    title: "Disconnect Embosser",
                    description: "Are you sure you want to disconnect this embosser?",
                    dialogActionButtonLabel: "Disconnect",
                    dialogActionButtonLoading: false,
                    dialogActionButtonOnClick: () => {},
                    inputType: "none",
                    inputLabel: "",
                    itemValue: '',
                    itemsList: [],
                    request: {
                      "request": "remoteSupport.setState",
                      "data": {
                        "state": false
                      }
                    },
                    requestData: false,
                    requestDataKey: "",
                    additionalSocketSignal: "",
                    additionalSocketSignalData: {},
                  })
                  dispatchEmbosserState({dialogOpen: true})
                }} 
                size="small"
              >
                Disconnect
              </Button>
            </CardActions>
          </Card>
        :
          <Box className={classes.embosserRow}>
            <PropertyArea
              property={"Model"}
              content={embosser.model !== '' ? embosser.model : "Unknown"}
              classes={{
                container: classes['embosserRowPropertyArea'],
                content:  classes['embosserRowPropertyAreaContent']
              }}
            />
            <PropertyArea
              property={"Serial"}
              content={embosser.serial !== '' ? embosser.serial : "Unknown"}
              classes={{
                container: classes['embosserRowPropertyArea'],
                content:  classes['embosserRowPropertyAreaContent']
              }}
            />
            <PropertyArea
                property={"Connected On"}
                content={embosser.connected !== undefined ? `${localStartDateTime.toLocaleDateString((navigator as any).language || (navigator as any).userLanguage, { year: 'numeric', month: 'short', day: 'numeric' })}, ${localStartDateTime.toLocaleTimeString()}` : "Unknown"}
                classes={{
                  container: classes['embosserRowPropertyArea'],
                  content:  classes['embosserRowPropertyAreaContent']
                }}
              />
            <Button
              variant="contained"
              className={classes.embosserRowButtonOpen}
              onClick={() => {
                dispatchEmbosserState({modalOpen: true})
              }}
              size="small"
              sx={{
                fontSize: `${width > 750 ? '.8em' : '.7em'}`
              }}
            >
              Open
            </Button>
            <Button
              variant="contained"
              color="error"
              className={classes.embosserRowButtonDisconnect}
              onClick={() => {
                dispatchDialogState({
                  title: "Disconnect Embosser",
                    description: "Are you sure you want to disconnect this embosser?",
                    dialogActionButtonLabel: "Disconnect",
                    dialogActionButtonLoading: false,
                    dialogActionButtonOnClick: () => {},
                    inputType: "none",
                    inputLabel: "",
                    itemValue: '',
                    itemsList: [],
                    request: {
                      "request": "remoteSupport.setState",
                      "data": {
                        "state": false
                      }
                    },
                    requestData: false,
                    requestDataKey: "",
                    additionalSocketSignal: "",
                    additionalSocketSignalData: {}
                })
                dispatchEmbosserState({dialogOpen: true})
              }} 
              size="small"
              sx={{
                fontSize: `${width > 750 ? '.8em' : '.7em'}`
              }}
            >
              Disconnect
            </Button>
          </Box>
      }
      <Modal
        open={embosserState.modalOpen}
        onClose={() => {
          dispatchEmbosserState({modalOpen: false})
        }}
      >
        <Box className={classes.modalContainer}>
          <Box 
            className={classes.modalScrollableContainer}
            sx={{
              flexDirection: width > 1200 ? "row" : "column",
              alignItems: width > 1200 ? "initial" : "center",
              overflowY: width <= 1200 ? "scroll" : "hidden",
              margin: `${width <= 1200 ? ".75em 1em 0em 0em !important" : ".75em .5em 0em 0em !important"}`
            }}
          >
            <Box
              className={classes.modalLeftPanel}
              sx={{
                width: width > 1200 ? "25%" : "100%",
                maxWidth: width > 1200 ? "400px" : "100%",
                minWidth: width > 1200 ? "300px !important" : "auto",
                height: width > 1200 ? "100% !important" : "auto !important"
              }}
            >
              {
                (width > 1200 || width < 400) ?
                  <>
                    <Card className={classes.modalEmbosserImageCard}>
                      <CardMedia
                        component="img"
                        alt={embosser.model}
                        src={deviceImage(embosser.model)}
                        className={classes.modalEmbosserImageCardMedia}
                        sx={{ height: "100%", width: "100%", objectFit: "contain" }}
                      />
                    </Card>
                    <PropertyArea
                      property={"Model"}
                      content={embosser.model !== '' ? embosser.model : "Unknown"}
                      classes={{
                        container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['leftPanelProperty']}`,
                        property: classes['propertyAreaProperty'],
                        content: classes['propertyAreaContent']
                      }}
                    />
                    <PropertyArea
                      property={"Serial"}
                      content={embosser.serial !== '' ? embosser.serial : "Unknown"}
                      classes={{
                        container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['leftPanelProperty']}`,
                        property: classes['propertyAreaProperty'],
                        content: classes['propertyAreaContent']
                      }}
                    />
                    <PropertyArea
                      property={"Type"}
                      content={embosser.type !== '' ? embosser.type : "Unknown"}
                      classes={{
                        container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['leftPanelProperty']}`,
                        property: classes['propertyAreaProperty'],
                        content: classes['propertyAreaContent']
                      }}
                    />
                    <PropertyArea
                      property={"Hammer Batch"}
                      content={embosser.hammerbatch !== '' ? embosser.hammerbatch : "Unknown"}
                      classes={{
                        container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['leftPanelProperty']}`,
                        property: classes['propertyAreaProperty'],
                        content: classes['propertyAreaContent']
                      }}
                      actionButton={embosser.firmware?.version === "3" || getLastUpdateVersion(embosser.firmware?.lastupdate) >= "v231124"}
                      actionButtonsList={[
                        {
                          tooltip: "Edit hammer batch",
                          disabled: false,
                          onClick: () => {
                            dispatchDialogState({
                              title: "Hammer Batch",
                              description: "Select the new hammer batch from the list below.",
                              dialogActionButtonLabel: "Save",
                              dialogActionButtonLoading: false,
                              dialogActionButtonOnClick: () => {},
                              inputType: "select",
                              inputLabel: "Hammer Batch",
                              itemValue: embosser.hammerbatch,
                              itemsList: ['1600', ...embosser.hammerBatchOptions.split(',')],
                              request: {
                                "request": "system.options.setHammerBatch",
                                "data": {
                                  "hammerBatch": dialogState.itemValue
                                } 
                              },
                              requestData: true,
                              requestDataKey: "hammerBatch",
                              additionalSocketSignal: "set_hammer_batch",
                              additionalSocketSignalData: {
                                "hammerBatch": dialogState.itemValue,
                                "id": embosser.id,
                                "serial": embosser.serial
                              }
                            })
                            dispatchEmbosserState({dialogOpen: true})
                          },
                          icon: <EditRounded />
                        }
                      ]}
                    />
                  </>
                :
                  <>
                    <PropertyArea
                      property={"Model & Serial"}
                      content={`${embosser.model !== '' ? embosser.model : "Unknown"} - ${embosser.serial !== '' ? embosser.serial : "Unknown"}`}
                      image={deviceImage(embosser.model)}
                      classes={{
                        container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['leftPanelProperty']}`,
                        property: classes['propertyAreaProperty'],
                        content: classes['propertyAreaContent']
                      }}
                    />
                    <PropertyArea
                      property={"Type"}
                      content={embosser.type !== '' ? embosser.type : "Unknown"}
                      classes={{
                        container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['leftPanelProperty']}`,
                        property: classes['propertyAreaProperty'],
                        content: classes['propertyAreaContent']
                      }}
                    />
                    <PropertyArea
                      property={"Hammer Batch"}
                      content={embosser.hammerbatch !== '' ? embosser.hammerbatch : "Unknown"}
                      classes={{
                        container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['leftPanelProperty']}`,
                        property: classes['propertyAreaProperty'],
                        content: classes['propertyAreaContent']
                      }}
                      actionButton={embosser.firmware?.version === "3" || getLastUpdateVersion(embosser.firmware?.lastupdate) >= "v231124"}
                      actionButtonsList={[
                        {
                          tooltip: "Edit hammer batch",
                          disabled: false,
                          onClick: () => {
                            dispatchDialogState({
                              title: "Hammer Batch",
                              description: "Select the new hammer batch from the list below.",
                              dialogActionButtonLabel: "Save",
                              dialogActionButtonLoading: false,
                              dialogActionButtonOnClick: () => {},
                              inputType: "select",
                              inputLabel: "Hammer Batch",
                              itemValue: embosser.hammerbatch,
                              itemsList: ['1600', ...embosser.hammerBatchOptions.split(',')],
                              request: {
                                "request": "system.options.setHammerBatch",
                                "data": {
                                  "hammerBatch": dialogState.itemValue
                                }
                              },
                              requestData: true,
                              requestDataKey: "hammerBatch",
                              additionalSocketSignal: "set_hammer_batch",
                              additionalSocketSignalData: {
                                "hammerBatch": dialogState.itemValue,
                                "id": embosser.id,
                                "serial": embosser.serial
                              }
                            })
                            dispatchEmbosserState({dialogOpen: true})
                          },
                          icon: <EditRounded />
                        }
                      ]}
                    />
                  </>
              }
              <Button
                variant="contained"
                className={classes.modalButtonOpenBrailleApp}
                onClick={() => {
                  const webInterfaceVersion = getWebInterfaceVersion(embosser.firmware?.lastupdate)
                  if (webInterfaceVersion! < webInterfaceVersions.at(0)!) {
                    dispatchDialogState({
                      title: "Open Web Interface",
                      description: "This embosser firmware is outdated. The web interface may not work as intended. Do you want to continue?",
                      dialogActionButtonLabel: "Continue",
                      dialogActionButtonLoading: false,
                      dialogActionButtonOnClick: () => {
                        const indexFile = `index-${embosser.model.toLowerCase().includes("aph") ? "aph" :
                        embosser.model.toLowerCase().includes("etc") ? "etc" : "indexbraille"}.html`
                        const url = `http://device.indexbraille.com/FW2/${webInterfaceVersions.at(0)!}/${indexFile}?p=${embosser.ports.ws}#`
                        window.open(url, '_blank')
                        dispatchEmbosserState({dialogOpen: false})
                      },
                      inputType: "none",
                      inputLabel: "",
                      itemValue: '',
                      itemsList: [],
                      request: undefined,
                      requestData: false,
                      requestDataKey: "",
                      additionalSocketSignal: "",
                      additionalSocketSignalData: {}
                    })
                    dispatchEmbosserState({dialogOpen: true})
                  } else {
                    const indexFile = `index-${embosser.model.toLowerCase().includes("aph") ? "aph" :
                    embosser.model.toLowerCase().includes("etc") ? "etc" : "indexbraille"}.html`
                    const url = `http://device.indexbraille.com/${embosser.firmware?.version === "3" ? "FW3" : "FW2"}/${embosser.firmware?.version === "3" ? "develop" : webInterfaceVersion}/${indexFile}?p=${embosser.ports.ws}#`
                    window.open(url, '_blank')
                  }
                }}
              >
                Open Web Interface
              </Button>
            </Box>
          {
            width < 1300 ?
              <Box
                className={classes.modalRightPanel}
                sx={{
                  width: width > 1200 ? "100%" : "95%",
                  mr: width > 1200 ? 2 : 0,
                  ml: width > 1200 ? 2 : 0,
                  overflow: width > 1200 ? "hidden" : "initial",
                  overflowY: width > 1200 ? "scroll" : "initial"
                }}
              >
                <MultiplePropertiesArea
                  topMargin={true}
                  property={"Firmware"}
                  classes={classes}
                  children={
                    <>
                      <PropertyArea
                        key={0}
                        property={"Timestamp"}
                        content={embosser.firmware.timestamp !== '' ? embosser.firmware.timestamp : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={1}
                        property={"Last Update"}
                        content={embosser.firmware.lastupdate !== '' ? embosser.firmware.lastupdate : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={2}
                        property={"Firmware Version"}
                        content={embosser.firmware.version === '3' ? embosser.firmware.version : "2"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={3}
                        property={"Web Interface Version"}
                        content={getWebInterfaceVersion(embosser.firmware?.lastupdate)!}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                    </>
                  }
                />
                <MultiplePropertiesArea
                  topMargin={true}
                  property={"Embosser Information"}
                  classes={classes}
                  children={
                    <>
                      <PropertyArea
                        key={0}
                        property={"Manufactured"}
                        content={embosser.manufactured !== '' ? embosser.manufactured : 'Unknown'}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginHorizontal']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={1}
                        property={"Delivered"}
                        content={embosser.delivered !== '' ? embosser.delivered : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginHorizontal']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={2}
                        property={"Tested By"}
                        content={embosser.tested.by !== '' ? embosser.tested.by : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={3}
                        property={"Tested On"}
                        content={embosser.tested.date !== '' ? embosser.tested.date : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                      key={4}
                        property={"Architecture"}
                        content={embosser.arch !== '' ? embosser.arch : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginHorizontal']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={5}
                        property={"Counter"}
                        content={embosser.counter !== '' ? embosser.counter : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaMarginBottom']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                    </>
                  }
                />
                <MultiplePropertiesArea
                  topMargin={true}
                  property={"Network"}
                  classes={classes}
                  children={
                    <>
                      {
                        embosser.macAddresses.bluetooth !== '' || embosser.macAddresses.ethernet !== '' || embosser.macAddresses.wifi !== '' ?
                          <>
                            <PropertyArea
                              key={0}
                              property={"Bluetooth"}
                              content={embosser.macAddresses.bluetooth !== '' ? embosser.macAddresses.bluetooth : "Unknown"}
                              classes={{
                                container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                                property: classes['propertyAreaProperty'],
                                content: classes['propertyAreaContent']
                              }}
                            />
                            <PropertyArea
                              key={1}
                              property={"Ethernet"}
                              content={embosser.macAddresses.ethernet !== '' ? embosser.macAddresses.ethernet : "Unknown"}
                              classes={{
                                container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                                property: classes['propertyAreaProperty'],
                                content: classes['propertyAreaContent']
                              }}
                            />
                            <PropertyArea
                              key={2}
                              property={"WiFi"}
                              content={embosser.macAddresses.wifi !== '' ? embosser.macAddresses.wifi : "Unknown"}
                              classes={{
                                container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                                property: classes['propertyAreaProperty'],
                                content: classes['propertyAreaContent']
                              }}
                            />
                          </>
                        :
                          <PropertyArea
                            key={0}
                            property={"Mac"}
                            content={embosser.mac !== '' ? embosser.mac : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                      }
                      <PropertyArea
                        key={embosser.macAddresses.bluetooth !== '' || embosser.macAddresses.ethernet !== '' || embosser.macAddresses.wifi !== '' ? 3 : 1}
                        property={"Hostname"}
                        content={embosser.hostname !== '' ? embosser.hostname : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']} ${classes['propertyAreaMarginHorizontal']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                    </>
                  }
                />
                <MultiplePropertiesArea
                  topMargin={true}
                  property={"Company"}
                  classes={classes}
                  children={
                    <>
                      <PropertyArea
                        key={0}
                        property={"Address"}
                        content={embosser.distributor.address !== '' ? embosser.distributor.address : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={1}
                        property={"Country Code"}
                        content={embosser.distributor.countrycode !== '' ? embosser.distributor.countrycode : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={2}
                        property={"Email"}
                        content={embosser.distributor.email !== '' ? embosser.distributor.email : "Unknown"}
                        classes={{
                            container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                            property: classes['propertyAreaProperty'],
                            content: classes['propertyAreaContent']
                          }}
                      />
                      <PropertyArea
                        key={3}
                        property={"Category"}
                        content={embosser.distributor.category !== '' ? embosser.distributor.category : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={4}
                        property={"Phone"}
                        content={embosser.distributor.phone !== '' ? embosser.distributor.phone : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={5}
                        property={"Name"}
                        content={embosser.distributor.name !== '' ? embosser.distributor.name : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={6}
                        property={"Id"}
                        content={embosser.distributor.id !== '' ? embosser.distributor.id : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                    </>
                  }
                />
                <MultiplePropertiesArea
                  topMargin={true}
                  property={"Connected"}
                  classes={classes}
                  children={
                    <>
                      <PropertyArea
                        key={0}
                        property={"Date"}
                        content={embosser.connected.date !== '' ? localStartDateTime.toLocaleDateString((navigator as any).language || (navigator as any).userLanguage, { year: 'numeric', month: 'short', day: 'numeric' }) : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={1}
                        property={"Time"}
                        content={embosser.connected.hour !== '' ? localStartDateTime.toLocaleTimeString() : "Unknown"}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                      />
                      <PropertyArea
                        key={2}
                        property={"Elapsed Time"}
                        content={elapsedTime}
                        classes={{
                          container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${embosser.firmware.version !== '3' ? classes['propertyAreaMarginBottom'] : ''}`,
                          property: classes['propertyAreaProperty'],
                          content: classes['propertyAreaContent']
                        }}
                        actionButton={true}
                        actionButtonsList={[
                          {
                            tooltip: "Reconnect",
                            disabled: reconnectRequested,
                            onClick: () => {
                              setReconnectRequested(true);
                              const reconnectRequest = {
                                "request": "remoteSupport.reconnect",
                              };
                              const secureEmbosserSocket = new WebSocket(`wss://device.indexbraille.com:8080/${embosser.ports.ws}`)
                              secureEmbosserSocket.onopen = () => {
                                console.log("Successfully connected to embosser through secure websocket.")
                                secureEmbosserSocket.send(JSON.stringify(reconnectRequest))
                              }
                              secureEmbosserSocket.onerror = (event) => {
                                socket.emit('connect_to_embosser', embosser.ports.ws, JSON.stringify(reconnectRequest))
                                const handleResponse = (response: any) => {
                                  socket.off('connect_to_embosser_response')
                                }
                                socket.on('connect_to_embosser_response', handleResponse)
                              }
                            },
                            icon: <ReplayRounded />
                          }
                        ]}
                      />
                      {
                        embosser.firmware.version === '3' ?
                          <PropertyArea
                            key={3}
                            property={"Time Remaining"}
                            content={timeRemaining}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                            actionButton={true}
                            actionButtonsList={[
                              {
                                tooltip: "Increase time remaining",
                                disabled: increaseTimeRemainingDelay,
                                onClick: () => {
                                  setIncreaseTimeRemainingDelay(true);
                                  const increaseTimeRemainingRequest = {
                                    "request": "remoteSupport.increaseTimeRemaining",
                                  };
                                  const secureEmbosserSocket = new WebSocket(`wss://device.indexbraille.com:8080/${embosser.ports.ws}`)
                                  secureEmbosserSocket.onopen = () => {
                                    console.log("Successfully connected to embosser through secure websocket.")
                                    secureEmbosserSocket.send(JSON.stringify(increaseTimeRemainingRequest))
                                    socket.emit('increase_time_remaining', embosser.id, embosser.serial, embosser.connected.timeRemaining)
                                  }
                                  secureEmbosserSocket.onerror = (event) => {
                                    socket.emit('connect_to_embosser', embosser.ports.ws, JSON.stringify(increaseTimeRemainingRequest))
                                    const handleResponse = (response: any) => {
                                      socket.off('connect_to_embosser_response')
                                    }
                                    socket.on('connect_to_embosser_response', handleResponse)
                                    socket.emit('increase_time_remaining', embosser.id, embosser.serial, embosser.connected.timeRemaining)
                                  }
                                },
                                icon: <AddCircleOutlineRounded />
                              }
                            ]}
                          />
                        : null
                      }
                    </>
                  }
                />
                {
                  currentRole !== undefined && currentRole.includes('Administrator') &&
                    <>
                      <MultiplePropertiesArea
                        topMargin={true}
                        property={"Ports"}
                        classes={classes}
                        children={
                          <>
                            <PropertyArea
                              key={0}
                              property={"SSH"}
                              content={embosser.ports.ssh !== '' ? embosser.ports.ssh : "Unknown"}
                              classes={{
                                container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                                property: classes['propertyAreaProperty'],
                                content: classes['propertyAreaContent']
                              }}
                              actionButton={true}
                              actionButtonsList={[
                                {
                                  tooltip: "Copy command",
                                  disabled: false,
                                  onClick: () => {
                                    if (window.isSecureContext) {
                                      navigator.clipboard.writeText(`ssh root@device.indexbraille.com -p ${embosser.ports.ssh}`)
                                      makeToast('success', `Command copied successfully`, mode)
                                    } else {
                                      makeToast('error', 'Could not copy to clipboard', mode)
                                    }
                                  },
                                  icon: <TerminalRounded />
                                },
                                {
                                  tooltip: "Copy port",
                                  disabled: false,
                                  onClick: () => {
                                    if (window.isSecureContext) {
                                      navigator.clipboard.writeText(`${embosser.ports.ssh}`)
                                      makeToast('success', `Port copied successfully`, mode)
                                    } else {
                                      makeToast('error', 'Could not copy to clipboard', mode)
                                    }
                                  },
                                  icon: <ContentCopyRounded />
                                }
                              ]}
                            />
                            <PropertyArea
                              key={1}
                              property={"WS"}
                              content={embosser.ports.ws !== '' ? embosser.ports.ws : "Unknown"}
                              classes={{
                                container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                                property: classes['propertyAreaProperty'],
                                content: classes['propertyAreaContent']
                              }}
                            />
                          </>
                        }
                      />
                      <MultiplePropertiesArea
                        topMargin={true}
                        property={"System Information"}
                        classes={classes}
                        children={
                          <>
                            <PropertyArea
                              property={"FID"}
                              content={embosser.fid !== '' ? embosser.fid : "Unknown"}
                              classes={{
                                container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                                property: classes['propertyAreaProperty'],
                                content: classes['propertyAreaContent']
                              }}
                            />
                            <PropertyArea
                              property={"Document ID"}
                              content={embosser.id !== '' ? embosser.id : "Unknown"}
                              classes={{
                                container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                                property: classes['propertyAreaProperty'],
                                content: classes['propertyAreaContent']
                              }}
                            />
                            <PropertyArea
                              property={"Protocol"}
                              content={embosser.protocol !== '' ? embosser.protocol : "Unknown"}
                              classes={{
                                container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                                property: classes['propertyAreaProperty'],
                                content: classes['propertyAreaContent']
                              }}
                            />
                          </>
                        }
                      />
                    </>
                }
              </Box>
            :
              <Box
                className={classes.modalRightPanelLarge}
                sx={{
                  flexGrow: "1",
                  overflow: width > 1200 ? "hidden" : "inherit",
                  overflowY: width > 1200 ? "scroll" : "inherit",
                  mr: width > 1200 ? 2 : 0,
                }}
              >
                <Box className={classes.modalRightPanelLargeScrollMask}>
                  <Box className={classes.modalRightPanelLargeLeftColumn}>
                    <MultiplePropertiesArea
                      topMargin={true}
                      property={"Firmware"}
                      classes={classes}
                      children={
                        <>
                          <PropertyArea
                            key={0}
                            property={"Timestamp"}
                            content={embosser.firmware.timestamp !== '' ? embosser.firmware.timestamp : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={1}
                            property={"Last Update"}
                            content={embosser.firmware.lastupdate !== '' ? embosser.firmware.lastupdate : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={2}
                            property={"Firmware Version"}
                            content={embosser.firmware.version === '3' ? embosser.firmware.version : "2"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={3}
                            property={"Web Interface Version"}
                            content={getWebInterfaceVersion(embosser.firmware?.lastupdate)!}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                        </>
                      }
                    />
                    <MultiplePropertiesArea
                      topMargin={true}
                      property={"Embosser Information"}
                      classes={classes}
                      children={
                        <>
                          <PropertyArea
                            key={0}
                            property={"Manufactured"}
                            content={embosser.manufactured !== '' ? embosser.manufactured : 'Unknown'}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginHorizontal']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={1}
                            property={"Delivered"}
                            content={embosser.delivered !== '' ? embosser.delivered : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginHorizontal']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={2}
                            property={"Tested By"}
                            content={embosser.tested.by !== '' ? embosser.tested.by : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={3}
                            property={"Tested On"}
                            content={embosser.tested.date !== '' ? embosser.tested.date : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                          key={4}
                            property={"Architecture"}
                            content={embosser.arch !== '' ? embosser.arch : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginHorizontal']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={5}
                            property={"Counter"}
                            content={embosser.counter !== '' ? embosser.counter : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaMarginBottom']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                        </>
                      }
                    />
                    <MultiplePropertiesArea
                      topMargin={true}
                      property={"Network"}
                      classes={classes}
                      children={
                        <>
                          {
                            embosser.macAddresses.bluetooth !== '' || embosser.macAddresses.ethernet !== '' || embosser.macAddresses.wifi !== '' ?
                              <>
                                <PropertyArea
                                  key={0}
                                  property={"Bluetooth"}
                                  content={embosser.macAddresses.bluetooth !== '' ? embosser.macAddresses.bluetooth : "Unknown"}
                                  classes={{
                                    container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                                    property: classes['propertyAreaProperty'],
                                    content: classes['propertyAreaContent']
                                  }}
                                />
                                <PropertyArea
                                  key={1}
                                  property={"Ethernet"}
                                  content={embosser.macAddresses.ethernet !== '' ? embosser.macAddresses.ethernet : "Unknown"}
                                  classes={{
                                    container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                                    property: classes['propertyAreaProperty'],
                                    content: classes['propertyAreaContent']
                                  }}
                                />
                                <PropertyArea
                                  key={2}
                                  property={"WiFi"}
                                  content={embosser.macAddresses.wifi !== '' ? embosser.macAddresses.wifi : "Unknown"}
                                  classes={{ 
                                    container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                                    property: classes['propertyAreaProperty'],
                                    content: classes['propertyAreaContent']
                                  }}
                                />
                              </>
                            :
                              <PropertyArea
                                key={0}
                                property={"Mac"}
                                content={embosser.mac !== '' ? embosser.mac : "Unknown"}
                                classes={{
                                  container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                                  property: classes['propertyAreaProperty'],
                                  content: classes['propertyAreaContent']
                                }}
                              />
                          }
                          <PropertyArea
                            key={embosser.macAddresses.bluetooth !== '' || embosser.macAddresses.ethernet !== '' || embosser.macAddresses.wifi !== '' ? 3 : 1}
                            property={"Hostname"}
                            content={embosser.hostname !== '' ? embosser.hostname : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']} ${classes['propertyAreaMarginHorizontal']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                        </>
                      }
                    />
                    <MultiplePropertiesArea
                      topMargin={true}
                      property={"Connected"}
                      classes={classes}
                      children={
                        <>
                          <PropertyArea
                            key={0}
                            property={"Date"}
                            content={embosser.connected.date !== '' ? localStartDateTime.toLocaleDateString((navigator as any).language || (navigator as any).userLanguage, { year: 'numeric', month: 'short', day: 'numeric' }) : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={1}
                            property={"Time"}
                            content={embosser.connected.hour !== '' ? localStartDateTime.toLocaleTimeString() : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={2}
                            property={"Elapsed Time"}
                            content={elapsedTime}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${embosser.firmware.version !== '3' ? classes['propertyAreaMarginBottom'] : ''}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                            actionButton={true}
                            actionButtonsList={[
                              {
                                tooltip: "Reconnect",
                                disabled: reconnectRequested,
                                onClick: () => {
                                  setReconnectRequested(true);
                                  const reconnectRequest = {
                                    "request": "remoteSupport.reconnect",
                                  };
                                  const secureEmbosserSocket = new WebSocket(`wss://device.indexbraille.com:8080/${embosser.ports.ws}`)
                                  secureEmbosserSocket.onopen = () => {
                                    console.log("Successfully connected to embosser through secure websocket.")
                                    secureEmbosserSocket.send(JSON.stringify(reconnectRequest))
                                  }
                                  secureEmbosserSocket.onerror = (event) => {
                                    socket.emit('connect_to_embosser', embosser.ports.ws, JSON.stringify(reconnectRequest))
                                    const handleResponse = (response: any) => {
                                      socket.off('connect_to_embosser_response')
                                    }
                                    socket.on('connect_to_embosser_response', handleResponse)
                                  }
                                },
                                icon: <ReplayRounded />
                              }
                            ]}
                          />
                          {
                            embosser.firmware.version === '3' ?
                              <PropertyArea
                                key={3}
                                property={"Time Remaining"}
                                content={timeRemaining}
                                classes={{
                                  container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                                  property: classes['propertyAreaProperty'],
                                  content: classes['propertyAreaContent']
                                }}
                                actionButton={true}
                                actionButtonsList={[
                                  {
                                    tooltip: "Increase time remaining",
                                    disabled: increaseTimeRemainingDelay,
                                    onClick: () => {
                                      setIncreaseTimeRemainingDelay(true);
                                      const increaseTimeRemainingRequest = {
                                        "request": "remoteSupport.increaseTimeRemaining",
                                      };
                                      const secureEmbosserSocket = new WebSocket(`wss://device.indexbraille.com:8080/${embosser.ports.ws}`)
                                      secureEmbosserSocket.onopen = () => {
                                        console.log("Successfully connected to embosser through secure websocket.")
                                        secureEmbosserSocket.send(JSON.stringify(increaseTimeRemainingRequest))
                                        socket.emit('increase_time_remaining', embosser.id, embosser.serial, embosser.connected.timeRemaining)
                                      }
                                      secureEmbosserSocket.onerror = (event) => {
                                        socket.emit('connect_to_embosser', embosser.ports.ws, JSON.stringify(increaseTimeRemainingRequest))
                                        const handleResponse = (response: any) => {
                                          socket.off('connect_to_embosser_response')
                                        }
                                        socket.on('connect_to_embosser_response', handleResponse)
                                        socket.emit('increase_time_remaining', embosser.id, embosser.serial, embosser.connected.timeRemaining)
                                      }
                                    },
                                    icon: <AddCircleOutlineRounded />
                                  }
                                ]}
                              />
                            : null
                          }
                        </>
                      }
                    />
                  </Box>
                  <Box className={classes.modalRightPanelLargeRightColumn}>
                    <MultiplePropertiesArea
                      topMargin={true}
                      property={"Company"}
                      classes={classes}
                      children={
                        <>
                          <PropertyArea
                            key={0}
                            property={"Address"}
                            content={embosser.distributor.address !== '' ? embosser.distributor.address : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={1}
                            property={"Country Code"}
                            content={embosser.distributor.countrycode !== '' ? embosser.distributor.countrycode : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={2}
                            property={"Email"}
                            content={embosser.distributor.email !== '' ? embosser.distributor.email : "Unknown"}
                            classes={{
                                container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                                property: classes['propertyAreaProperty'],
                                content: classes['propertyAreaContent']
                              }}
                          />
                          <PropertyArea
                            key={3}
                            property={"Category"}
                            content={embosser.distributor.category !== '' ? embosser.distributor.category : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={4}
                            property={"Phone"}
                            content={embosser.distributor.phone !== '' ? embosser.distributor.phone : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={5}
                            property={"Name"}
                            content={embosser.distributor.name !== '' ? embosser.distributor.name : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                          <PropertyArea
                            key={6}
                            property={"Id"}
                            content={embosser.distributor.id !== '' ? embosser.distributor.id : "Unknown"}
                            classes={{
                              container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                              property: classes['propertyAreaProperty'],
                              content: classes['propertyAreaContent']
                            }}
                          />
                        </>
                      }
                    />
                    {
                      currentRole !== undefined && currentRole.includes('Administrator') &&
                        <>
                          <MultiplePropertiesArea
                            topMargin={true}
                            property={"Ports"}
                            classes={classes}
                            children={
                              <>
                                <PropertyArea
                                  key={0}
                                  property={"SSH"}
                                  content={embosser.ports.ssh !== '' ? embosser.ports.ssh : "Unknown"}
                                  classes={{
                                    container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaSmallTopMargin']}`,
                                    property: classes['propertyAreaProperty'],
                                    content: classes['propertyAreaContent']
                                  }}
                                  actionButton={true}
                                  actionButtonsList={[
                                    {
                                      tooltip: "Copy command",
                                      disabled: false,
                                      onClick: () => {
                                        if (window.isSecureContext) {
                                          navigator.clipboard.writeText(`ssh root@device.indexbraille.com -p ${embosser.ports.ssh}`)
                                          makeToast('success', `Command copied successfully`, mode)
                                        } else {
                                          makeToast('error', 'Could not copy to clipboard', mode)
                                        }
                                      },
                                      icon: <TerminalRounded />
                                    },
                                    {
                                      tooltip: "Copy port",
                                      disabled: false,
                                      onClick: () => {
                                        if (window.isSecureContext) {
                                          navigator.clipboard.writeText(`${embosser.ports.ssh}`)
                                          makeToast('success', `Port copied successfully`, mode)
                                        } else {
                                          makeToast('error', 'Could not copy to clipboard', mode)
                                        }
                                      },
                                      icon: <ContentCopyRounded />
                                    }
                                  ]}
                                />
                                <PropertyArea
                                  key={1}
                                  property={"WS"}
                                  content={embosser.ports.ws !== '' ? embosser.ports.ws : "Unknown"}
                                  classes={{
                                    container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                                    property: classes['propertyAreaProperty'],
                                    content: classes['propertyAreaContent']
                                  }}
                                />
                              </>
                            }
                          />
                          <MultiplePropertiesArea
                            topMargin={true}
                            property={"System Information"}
                            classes={classes}
                            children={
                              <>
                                <PropertyArea
                                  property={"FID"}
                                  content={embosser.fid !== '' ? embosser.fid : "Unknown"}
                                  classes={{
                                    container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                                    property: classes['propertyAreaProperty'],
                                    content: classes['propertyAreaContent']
                                  }}
                                />
                                <PropertyArea
                                  property={"Document ID"}
                                  content={embosser.id !== '' ? embosser.id : "Unknown"}
                                  classes={{
                                    container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']}`,
                                    property: classes['propertyAreaProperty'],
                                    content: classes['propertyAreaContent']
                                  }}
                                />
                                <PropertyArea
                                  property={"Protocol"}
                                  content={embosser.protocol !== '' ? embosser.protocol : "Unknown"}
                                  classes={{
                                    container: `${classes['propertyArea']} ${classes['propertyAreaMarginHorizontal']} ${classes['propertyAreaTopMargin']} ${classes['propertyAreaMarginBottom']}`,
                                    property: classes['propertyAreaProperty'],
                                    content: classes['propertyAreaContent']
                                  }}
                                />
                              </>
                            }
                          />
                        </>
                    }
                  </Box>
                </Box>
              </Box>
          }
          </Box>
          <IconButton
            className={classes.modalButtonClose}
            onClick={() => {
              dispatchEmbosserState({modalOpen: false})
            }}
          >
            <CloseRounded />
          </IconButton>
        </Box>
      </Modal>
      <Dialog
        open={embosserState.dialogOpen}
        PaperProps={{
          style: {
            backgroundColor: mode === 'light' ? "#f6f6f6" : "#252525",
            borderRadius: "10px",
            boxShadow: mode === 'light' ? "0px 3px 12px -1px rgba(0, 0, 0, 0.2)" : "0px 3px 12px -1px rgba(0, 0, 0, 0.5)"
          }
        }}
        onClose={() => dispatchEmbosserState({dialogOpen: false})}
        fullWidth
        maxWidth="xs"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {dialogState.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {dialogState.description}
          </DialogContentText>
          {dialogInput}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={dialogState.dialogActionButtonLoading}
            onClick={() => dispatchEmbosserState({dialogOpen: false})}
            color="error"
          >
            Cancel
          </Button>
          {
            dialogState.request === undefined ?
              <Button
                onClick={dialogState.dialogActionButtonOnClick}
                autoFocus
                color="secondary"
              >
                {dialogState.dialogActionButtonLabel}
              </Button>
            :
              <LoadingButton
                loading={dialogState.dialogActionButtonLoading}
                onClick={() => {
                  dispatchDialogState({dialogActionButtonLoading: true})
                  const secureEmbosserSocket = new WebSocket(`wss://device.indexbraille.com:8080/${embosser.ports.ws}`)
                  const timeout = setTimeout(() => {
                    secureEmbosserSocket.close()
                    socket.off("additional_signal_response")
                    socket.off("connect_to_embosser_response")
                    dispatchDialogState({dialogActionButtonLoading: false})
                    dispatchEmbosserState({dialogOpen: false})
                    makeToast("error", "Could not complete request", mode)
                  }, 90000)
                  secureEmbosserSocket.onopen = () => {
                    console.log("Successfully connected to embosser through secure websocket.")
                    secureEmbosserSocket.send(JSON.stringify(dialogState.request))
                  }
                  secureEmbosserSocket.onmessage = (response: any) => {
                    const responseObject = JSON.parse(response)
                    if (responseObject.status === "success") {
                      if (dialogState.additionalSocketSignal !== "") {
                        socket.emit(dialogState.additionalSocketSignal, JSON.stringify(dialogState.additionalSocketSignalData))
                        socket.on("additional_signal_response", (response: any) => {
                          if (response.status === "success") {
                            clearTimeout(timeout)
                            secureEmbosserSocket.close()
                            dispatchDialogState({dialogActionButtonLoading: false})
                            dispatchEmbosserState({dialogOpen: false})
                            makeToast("success", response.message, mode)
                          } else {
                            clearTimeout(timeout)
                            secureEmbosserSocket.close()
                            dispatchDialogState({dialogActionButtonLoading: false})
                            dispatchEmbosserState({dialogOpen: false})
                            makeToast("error", response.message, mode)
                          }
                          socket.off("additional_signal_response")
                        })
                      } else {
                        clearTimeout(timeout)
                        secureEmbosserSocket.close()
                        dispatchDialogState({dialogActionButtonLoading: false})
                        dispatchEmbosserState({dialogOpen: false})
                        makeToast("success", "Request completed successfully", mode)
                      }
                    } else {
                      clearTimeout(timeout)
                      secureEmbosserSocket.close()
                      dispatchDialogState({dialogActionButtonLoading: false})
                      dispatchEmbosserState({dialogOpen: false})
                      makeToast("error", response.result, mode)
                    }
                  }
                  secureEmbosserSocket.onerror = (event) => {
                    socket.emit('connect_to_embosser', embosser.ports.ws, JSON.stringify(dialogState.request))
                    const handleResponse = (response: any) => {
                      const responseObject = JSON.parse(response)
                      if (responseObject.status === "success") {
                        if (dialogState.additionalSocketSignal !== "") {
                          socket.emit(dialogState.additionalSocketSignal, JSON.stringify(dialogState.additionalSocketSignalData))
                          socket.on("additional_signal_response", (response: any) => {
                            if (response.status === "success") {
                              clearTimeout(timeout)
                              dispatchDialogState({dialogActionButtonLoading: false})
                              dispatchEmbosserState({dialogOpen: false})
                              makeToast("success", response.message, mode)
                            } else {
                              clearTimeout(timeout)
                              dispatchDialogState({dialogActionButtonLoading: false})
                              dispatchEmbosserState({dialogOpen: false})
                              makeToast("error", response.message, mode)
                            }
                            socket.off("additional_signal_response")
                          })
                        } else {
                          clearTimeout(timeout)
                          dispatchDialogState({dialogActionButtonLoading: false})
                          dispatchEmbosserState({dialogOpen: false})
                          makeToast("success", "Request completed successfully", mode)
                        }
                      } else {
                        clearTimeout(timeout)
                        dispatchDialogState({dialogActionButtonLoading: false})
                        dispatchEmbosserState({dialogOpen: false})
                        makeToast("error", response.result, mode)
                      }
                      socket.off('connect_to_embosser_response')
                    }
                    socket.on('connect_to_embosser_response', handleResponse)
                  }
                }}
                autoFocus
                color="secondary"
              >
                {dialogState.dialogActionButtonLabel}
              </LoadingButton>
          }
        </DialogActions>
      </Dialog>
    </>
  )
}